import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageMeta from "@/components/feature/PageMeta";
import PosterGrid from "@/components/base/PosterGrid";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/feature/FilterBar";
import {
  ALL,
  ITEMS_PER_PAGE,
  defaultFilter,
  queryTitles,
  type FilterState,
  type SortKey,
} from "@/utils/catalog";
import type { MediaType } from "@/mocks/titles";

const COPY: Record<MediaType, { name: string; desc: string; keyword: string }> = {
  movie: {
    name: "电影",
    desc: "收录院线热片、经典老片与高分佳作，全部整理为中文字幕版本，支持按地区、年份与题材逐层筛选。",
    keyword: "中文字幕电影,电影在线观看,高清电影",
  },
  tv: {
    name: "电视剧",
    desc: "收录热播剧集、年代大戏与悬疑刑侦剧，标注集数与单集时长，中文字幕同步整理，追剧不迷路。",
    keyword: "电视剧在线观看,中文字幕剧集,热播电视剧",
  },
};

export default function CategoryPage() {
  const params = useParams<{ type: string }>();
  const location = useLocation();
  const showFilter = location.pathname.endsWith("/filter");
  const type = params.type === "tv" ? "tv" : params.type === "movie" ? "movie" : null;

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilter(defaultFilter);
    setSort("latest");
    setPage(1);
  }, [params.type]);

  const results = useMemo(
    () => (type ? queryTitles({ type, filter, sort }) : []),
    [type, filter, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const copy = type ? COPY[type] : COPY.movie;

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const handleSortChange = (next: SortKey) => {
    setSort(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilter(defaultFilter);
    setSort("latest");
    setPage(1);
  };

  if (!type) {
    return (
      <Layout>
        <PageMeta
          title="分类不存在 - 中文字幕在线免费看"
          description="你访问的影视分类不存在，请返回首页或进入电影、电视剧频道继续浏览。"
        />
        <div className="mx-auto flex max-w-[760px] flex-col items-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-background-100">
            <i className="ri-folder-warning-line text-2xl text-primary-600" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">该分类暂时不存在</h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            目前站内仅提供「电影」与「电视剧」两个分类，你可以从下方入口继续浏览。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            <Link to="/category/movie" className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700">电影频道</Link>
            <Link to="/category/tv" className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700">电视剧频道</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageMeta
        title={`${copy.name}分类 - 在线观看 - 中文字幕在线免费看`}
        description={`${copy.name}分类页面汇集站内全部中文字幕${copy.name}作品，${copy.desc}`}
        keywords={copy.keyword}
      />

      <div className="border-b border-background-200 bg-background-100 px-4 py-7 md:px-8 md:py-9">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
          {showFilter ? (
            <>
              <Link to={`/category/${type}`} className="cursor-pointer transition-colors hover:text-primary-700">{copy.name}</Link>
              <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
              <span className="text-foreground-800">条件筛选</span>
            </>
          ) : (
            <span className="text-foreground-800">{copy.name}</span>
          )}
        </nav>

        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {copy.name}分类
              <span className="ml-2 text-sm font-normal text-foreground-500">中文字幕在线观看</span>
            </h1>
            <p className="mt-2 max-w-[720px] text-xs leading-6 text-foreground-600 md:text-sm">{copy.desc}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Link
              to={`/category/movie${showFilter ? "/filter" : ""}`}
              className={`cursor-pointer whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${type === "movie" ? "bg-primary-600 text-background-50" : "bg-background-50 text-foreground-700"}`}
            >
              电影
            </Link>
            <Link
              to={`/category/tv${showFilter ? "/filter" : ""}`}
              className={`cursor-pointer whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${type === "tv" ? "bg-primary-600 text-background-50" : "bg-background-50 text-foreground-700"}`}
            >
              电视剧
            </Link>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 md:px-8 md:py-8">
        <FilterBar
          filter={filter}
          sort={sort}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onReset={handleReset}
          defaultOpen={showFilter}
        />

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-600">
          <p>
            共找到 <span className="font-medium text-foreground-950">{results.length}</span> 部{copy.name}
            {filter.region !== ALL ? ` · ${filter.region}` : ""}
            {filter.year !== ALL ? ` · ${filter.year} 年` : ""}
            {filter.genre !== ALL ? ` · ${filter.genre}` : ""}
          </p>
          <p className="text-foreground-500">每页展示 {ITEMS_PER_PAGE} 部</p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-4">
              <PosterGrid items={pageItems} />
            </div>
            <Pagination page={currentPage} totalPages={totalPages} total={results.length} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-search-eye-line text-2xl text-primary-500" aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有符合条件的作品</h2>
            <p className="mt-2 max-w-[420px] text-xs leading-6 text-foreground-600">
              当前的地区、年份与题材组合暂时没有匹配内容，试试放宽条件，或直接重置筛选查看全部{copy.name}。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}